///
/// EarthBrowser
///
/// Created by Matt Giger on 11/12/2023
/// Copyright (c) 2023 Geodata Labs
///

/**
 * A class containing trigonometric constants for convenient use in calculations.
 */
export class trig {
	/** The mathematical constant pi (π) */
	public static pi: number					= 3.141592653589793
	/** The reciprocal of pi (1/π) */
	public static pi_recip: number				= 1.0 / trig.pi
	/** One quarter of pi (π/4) */
	public static quarter_pi: number			= trig.pi / 4.0
	/** Half of pi (π/2) */
	public static half_pi: number				= trig.pi / 2.0
	/** The reciprocal of half pi (2/π) */
	public static half_pi_recip: number			= 2.0 / trig.pi
	/** Two times pi (2π) */
	public static two_pi: number				= trig.pi * 2.0
	/** The reciprocal of two pi (1/2π) */
	public static two_pi_recip: number			= 1.0 / trig.two_pi
	/** Conversion factor from radians to degrees */
	public static deg: number					= 180.0 / trig.pi
	/** Conversion factor from degrees to radians */
	public static rad: number					= trig.pi / 180.0
}

/**
 * Rounds a number to a specified number of decimal places.
 * @param value - The number to round.
 * @param positions - The number of decimal places to round to.
 * @returns The rounded number.
 */
export function round(value: number, positions: number): number {
	const exp = Math.pow(10, Math.floor(positions))
	const val = Math.floor(value * exp)
	const ret = val / exp
	return ret
}

/**
 * Calculates the floating-point remainder of the division of two numbers.
 * This function implements the modulo operation for floating-point numbers.
 *
 * @param a - The dividend (number to be divided).
 * @param b - The divisor (number to divide by).
 * @returns The remainder of the division of a by b.
 */
export function fmod(a: number, b: number): number {
	return a - Math.floor(a / b) * b
}

/**
 * Clamps a value between a lower and upper bound.
 * @param value - The value to clamp.
 * @param low - The lower bound.
 * @param high - The upper bound.
 * @returns The clamped value: low if value < low, high if value > high, otherwise value.
 */
export function clamp(value: number, low: number, high: number): number {
	return (value < low) ? low : (value > high) ? high : value
}

/**
 * Linearly interpolates between two values.
 * @param x - The start value.
 * @param y - The end value.
 * @param a - The interpolation factor (0 to 1).
 * @returns The interpolated value.
 */
export function mix(x: number, y: number, a: number): number {
	return x * (1-a) + y*a
}

/**
 * Linearly interpolates between two values.
 * @param x - The start value.
 * @param y - The end value.
 * @param a - The interpolation factor (0 to 1).
 * @returns The interpolated value.
 */
export function lerp(x: number, y: number, a: number): number {
	return x * (1-a) + y*a
}

/**
 * Checks if two numbers are equal within a specified epsilon.
 * @param x - The first number.
 * @param y - The second number.
 * @param epsilon - The epsilon value (default: 0.0001).
 * @returns True if the numbers are equal, false otherwise.
 */
export function equals(x: number, y: number, epsilon: number = 0.0001): boolean {
	return Math.abs(x - y) < epsilon
}